import { TutorialLayout, CodePreview } from '../../../../shared/components';
import { tutorialLevels } from '../../../../shared/config/tutorials';
import { Alert, AlertDescription } from '@voilajsx/uikit/alert';
import { Card, CardContent, CardHeader, CardTitle } from '@voilajsx/uikit/card';
import { FolderTree, Zap } from 'lucide-react';

const FBCATemplate = () => {
  return (
    <TutorialLayout levels={tutorialLevels}>
      <div className="space-y-8">
        <div>
          <h1 className="text-3xl font-bold mb-4">FBCA Template</h1>
          <p className="text-lg text-muted-foreground">
            Feature-Based Component Architecture with auto-discovery routing.
          </p>
        </div>

        <Alert>
          <Zap className="w-4 h-4" />
          <AlertDescription>
            Best for: Large applications, enterprise projects, and teams that want feature isolation with automatic routing.
          </AlertDescription>
        </Alert>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">Create a Project</h2>

          <CodePreview
            title="Scaffold FBCA Template"
            code={`npx @voilajsx/uikit create my-app --fbca

cd my-app
npm run dev`}
            language="bash"
          />
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">Project Structure</h2>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <FolderTree className="w-5 h-5" />
                Directory Layout
              </CardTitle>
            </CardHeader>
            <CardContent>
              <pre className="text-sm font-mono bg-muted p-4 rounded-lg overflow-x-auto">
{`my-app/
├── src/
│   └── web/
│       ├── features/              # Feature modules
│       │   ├── main/              # Main/home feature
│       │   │   ├── components/    # Feature-specific components
│       │   │   └── pages/         # Route pages
│       │   │       └── index.tsx  # → /
│       │   ├── dashboard/
│       │   │   ├── components/
│       │   │   └── pages/
│       │   │       ├── index.tsx  # → /dashboard
│       │   │       └── settings.tsx  # → /dashboard/settings
│       │   └── auth/
│       │       └── pages/
│       │           ├── login.tsx  # → /auth/login
│       │           └── register.tsx
│       ├── shared/                # Shared across features
│       │   ├── components/
│       │   ├── hooks/
│       │   └── utils/
│       ├── App.tsx               # Auto-discovery router
│       └── main.tsx
├── index.html
└── package.json`}
              </pre>
            </CardContent>
          </Card>
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">Auto-Discovery Routing</h2>
          <p className="text-muted-foreground">
            FBCA uses <code>import.meta.glob</code> to automatically discover and register routes based on file structure.
          </p>

          <CodePreview
            title="How Routes Are Generated"
            code={`// File                                  → Route
features/main/pages/index.tsx           → /
features/dashboard/pages/index.tsx      → /dashboard
features/dashboard/pages/settings.tsx   → /dashboard/settings
features/auth/pages/login.tsx           → /auth/login
features/blog/pages/[slug].tsx          → /blog/:slug (dynamic)
features/users/pages/[id]/edit.tsx      → /users/:id/edit`}
            language="text"
          />

          <CodePreview
            title="src/web/App.tsx (Auto-Discovery)"
            code={`import { BrowserRouter, Routes, Route } from 'react-router-dom';

// Auto-discover all page components
const pages = import.meta.glob('./features/**/pages/**/*.tsx', { eager: true });

function App() {
  const routes = Object.entries(pages).map(([path, module]) => {
    // Convert file path to route path
    // ./features/dashboard/pages/settings.tsx → /dashboard/settings
    const routePath = path
      .replace('./features/', '/')
      .replace('/pages', '')
      .replace('/index.tsx', '')
      .replace('.tsx', '')
      .replace(/\[(\w+)\]/g, ':$1'); // [id] → :id

    const Component = (module as any).default;

    return { path: routePath || '/', Component };
  });

  return (
    <BrowserRouter>
      <Routes>
        {routes.map(({ path, Component }) => (
          <Route key={path} path={path} element={<Component />} />
        ))}
      </Routes>
    </BrowserRouter>
  );
}

export default App;`}
            language="tsx"
          />
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">Feature Module Structure</h2>

          <CodePreview
            title="features/dashboard/pages/index.tsx"
            code={`import { AdminLayout } from '@voilajsx/uikit/admin-layout';
import { Card, CardContent, CardHeader, CardTitle } from '@voilajsx/uikit/card';
import Sidebar from '../../shared/components/Sidebar';
import Header from '../../shared/components/Header';

function DashboardPage() {
  return (
    <AdminLayout sidebar={<Sidebar />} header={<Header />}>
      <div className="p-6">
        <h1 className="text-2xl font-bold mb-6">Dashboard</h1>
        <div className="grid md:grid-cols-3 gap-4">
          <Card>
            <CardHeader>
              <CardTitle>Total Users</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">12,345</div>
            </CardContent>
          </Card>
          {/* More cards */}
        </div>
      </div>
    </AdminLayout>
  );
}

export default DashboardPage;`}
            language="tsx"
          />
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">Dynamic Routes</h2>

          <CodePreview
            title="features/blog/pages/[slug].tsx"
            code={`import { useParams } from 'react-router-dom';
import { PageLayout } from '@voilajsx/uikit/page-layout';

function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>();

  return (
    <PageLayout>
      <article className="max-w-3xl mx-auto py-12 px-4">
        <h1 className="text-4xl font-bold mb-4">
          Blog Post: {slug}
        </h1>
        {/* Fetch and render post content */}
      </article>
    </PageLayout>
  );
}

export default BlogPostPage;`}
            language="tsx"
          />
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">Shared Components</h2>

          <CodePreview
            title="shared/components/Header.tsx"
            code={`// Components in shared/ are available to all features
import { Link } from 'react-router-dom';
import { Button } from '@voilajsx/uikit/button';
import { useTheme } from '@voilajsx/uikit/theme-provider';
import { Moon, Sun } from 'lucide-react';

function Header() {
  const { mode, toggleMode } = useTheme();

  return (
    <header className="h-16 border-b border-border px-6 flex items-center justify-between">
      <Link to="/" className="font-bold text-xl">My App</Link>
      <Button variant="ghost" size="icon" onClick={toggleMode}>
        {mode === 'dark' ? <Sun /> : <Moon />}
      </Button>
    </header>
  );
}

export default Header;`}
            language="tsx"
          />
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">Template Comparison</h2>

          <div className="bg-muted/50 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left p-3 font-semibold">Feature</th>
                  <th className="text-left p-3 font-semibold">Multi</th>
                  <th className="text-left p-3 font-semibold">FBCA</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-border">
                  <td className="p-3">Route definition</td>
                  <td className="p-3 text-muted-foreground">Manual config</td>
                  <td className="p-3 text-muted-foreground">Auto-discovery</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="p-3">Adding new page</td>
                  <td className="p-3 text-muted-foreground">Create file + add route</td>
                  <td className="p-3 text-muted-foreground">Just create file</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="p-3">Feature isolation</td>
                  <td className="p-3 text-muted-foreground">Manual</td>
                  <td className="p-3 text-muted-foreground">Built-in</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="p-3">Scale</td>
                  <td className="p-3 text-muted-foreground">Medium apps</td>
                  <td className="p-3 text-muted-foreground">Large apps</td>
                </tr>
                <tr>
                  <td className="p-3">Learning curve</td>
                  <td className="p-3 text-muted-foreground">Lower</td>
                  <td className="p-3 text-muted-foreground">Slightly higher</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">When to Use FBCA</h2>

          <div className="grid md:grid-cols-2 gap-4">
            <Card className="border-green-500/50">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg text-green-600">Ideal For</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li>• Large applications (20+ pages)</li>
                  <li>• Multiple feature teams</li>
                  <li>• Want convention over configuration</li>
                  <li>• Need feature isolation</li>
                  <li>• Enterprise projects</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-yellow-500/50">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg text-yellow-600">Consider Simpler</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li>• Small/simple apps → SPA template</li>
                  <li>• Need explicit route control → Multi template</li>
                  <li>• Learning React Router → Multi template</li>
                  <li>• Quick prototypes → Single/SPA template</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </section>
      </div>
    </TutorialLayout>
  );
};

export default FBCATemplate;
